import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SMS_CONSENT_TEXT } from "@/lib/consent";

export default function LeadDialog() {
  const { config, lead, closeLead } = useSite();
  const dialogRef = useRef(null);
  const statusRef = useRef(null);
  const isHome = lead.extra?.audience === "home";
  const [form, setForm] = useState({ name: "", email: "", phone: "", monthlyBill: "", priority: "Lower energy costs", consent: false, marketing: false, smsConsent: false, street: "", city: "", zip: "" });
  const [bill, setBill] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [result, setResult] = useState(null);
  const [idem, setIdem] = useState("");

  useEffect(() => {
    if (lead.open) {
      setForm({ name: "", email: "", phone: "", monthlyBill: "", priority: "Lower energy costs", consent: false, marketing: false, smsConsent: false, street: "", city: "", zip: "" });
      setBill(null);
      setStatus("");
      setResult(null);
      setBusy(false);
      setIdem(`${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [lead.open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setStatus("");
    try {
      let uploadIds = [];
      if (bill) {
        const fd = new FormData();
        fd.append("file", bill);
        const up = await api.post("/uploads", fd).catch((err) => { throw new Error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); });
        uploadIds = [up.data.id];
      }
      const payload = {
        serviceId: lead.serviceId || "contact",
        audience: lead.extra?.audience || "business",
        name: form.name,
        email: form.email,
        phone: form.phone,
        contactConsent: { granted: form.consent, policyVersion: config.policyVersion, recordedAt: new Date().toISOString() },
        smsConsent: { granted: form.smsConsent, text: SMS_CONSENT_TEXT, recordedAt: new Date().toISOString() },
        marketingConsent: form.marketing,
        contextService: lead.contextService || lead.serviceId,
        serviceAnswer: lead.extra?.serviceAnswer || (isHome ? form.priority : null),
        address: isHome && form.street
          ? `${form.street}, ${form.city}, FL ${form.zip}`
          : lead.extra?.address || null,
        monthlyBill: form.monthlyBill ? Number(form.monthlyBill) : null,
        dimensions: lead.extra?.dimensions || null,
        layoutSourceMode: lead.extra?.layoutSourceMode || null,
        roofSegments: lead.extra?.roofSegments || null,
        storageInterest: lead.extra?.storageInterest || null,
        uploadIds,
        attribution: { page: lead.extra?.page || window.location.pathname },
      };
      const res = await api.post("/leads", payload, { headers: { "Idempotency-Key": idem } }).catch((err) => { throw new Error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed"); });
      setResult(res.data);
      setTimeout(() => statusRef.current?.focus(), 30);
    } catch (err) {
      setStatus(err.message);
      setTimeout(() => statusRef.current?.focus(), 30);
    } finally {
      setBusy(false);
    }
  }

  const pending = result?.emailStatus === "pending_recipients";
  return (
    <dialog ref={dialogRef} data-testid="lead-dialog" role="dialog" aria-modal="true" aria-labelledby="lead-title" onClick={(e) => { if (e.target === dialogRef.current) dialogRef.current.close(); }}>
      <button className="close" aria-label="Close dialog" data-testid="lead-dialog-close" onClick={closeLead}>×</button>
      <span className="eyebrow">YOUR PROPERTY. YOUR PLAN.</span>
      <h2 id="lead-title">Let’s take a closer look.</h2>
      <p>
        {lead.contextService ? `${lead.contextService} · ` : ""}
        {isHome ? "Home property" : "Business property"}
      </p>
      {result ? (
        <div data-testid="lead-success" role="status" tabIndex={-1} ref={statusRef}>
          <p className="status" style={{ fontSize: 14 }}>
            Request received. Your reference is <strong>{result.reference}</strong>. Keep it for your records — it identifies your saved project.
          </p>
          {pending && <p className="micro">Our notification to the project team is still being connected, so for the fastest response call <a href={config.phoneHref}>{config.phone}</a>.</p>}
          <button className="btn" onClick={closeLead}>Done</button>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="lead-form">
          <div className="twofields">
            <label className="field">
              Full name
              <input required maxLength={100} autoComplete="name" data-testid="lead-name" value={form.name} onChange={set("name")} />
            </label>
            <label className="field">
              Email
              <input required type="email" maxLength={254} autoComplete="email" data-testid="lead-email" value={form.email} onChange={set("email")} />
            </label>
          </div>
          <div className="twofields">
            <label className="field">
              Phone
              <input required type="tel" maxLength={30} autoComplete="tel" data-testid="lead-phone" value={form.phone} onChange={set("phone")} />
            </label>
            <label className="field">
              Monthly electric bill ($)
              <input type="number" min={0} max={10000000} data-testid="lead-bill" value={form.monthlyBill} onChange={set("monthlyBill")} />
            </label>
          </div>
          {isHome ? (
            <div>
              <label className="field">
                Home address — street
                <input required maxLength={200} autoComplete="street-address" data-testid="lead-home-street" placeholder="Street address" value={form.street} onChange={set("street")} />
              </label>
              <div className="twofields">
                <label className="field">
                  City
                  <input required maxLength={100} autoComplete="address-level2" data-testid="lead-home-city" value={form.city} onChange={set("city")} />
                </label>
                <label className="field">
                  ZIP
                  <input required maxLength={5} inputMode="numeric" autoComplete="postal-code" data-testid="lead-home-zip" value={form.zip} onChange={set("zip")} />
                </label>
              </div>
            </div>
          ) : null}
          <label className="field">
            Your main priority
            <select data-testid="lead-priority" value={form.priority} onChange={set("priority")}>
              <option>Lower energy costs</option>
              <option>Backup power</option>
              <option>Roof and solar together</option>
              <option>Upgrade my property</option>
            </select>
          </label>
          <label className="upload">
            Add a utility bill (optional, PDF/JPG/PNG under 15 MB)
            <input type="file" accept="application/pdf,image/jpeg,image/png" data-testid="lead-bill-upload" onChange={(e) => setBill(e.target.files[0] || null)} />
          </label>
          <input className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" name="company_website" value="" readOnly />
          <label className="check">
            <input type="checkbox" required data-testid="lead-consent" checked={form.consent} onChange={set("consent")} />
            <span>
              I agree to be contacted about my request and have reviewed the <Link to="/privacy" target="_blank">privacy information</Link>. Marketing enrollment is separate.
            </span>
          </label>
          <label className="check" data-testid="lead-sms-consent-row">
            <input type="checkbox" data-testid="lead-sms-consent" checked={form.smsConsent} onChange={set("smsConsent")} />
            <span>{SMS_CONSENT_TEXT}</span>
          </label>
          <button className="btn" type="submit" disabled={busy} data-testid="lead-submit">
            {busy ? "Sending securely…" : "Send my quote request"} <span>↗</span>
          </button>
          <p className="micro">Your request is saved with a reference before anything else happens. Marketing messages require a separate opt-in.</p>
        </form>
      )}
      {status && !result && (
        <p className="status" role="alert" tabIndex={-1} ref={statusRef} style={{ color: "#a33a17" }}>
          {status} Or call <a href={config.phoneHref}>{config.phone}</a>.
        </p>
      )}
    </dialog>
  );
}
